<svelte:options runes={false} />
<script>
  // TCA tab (TASK-K2T-15, ADR-0030): execution measurement against the INDEPENDENT quote-tape
  // witness — implementation shortfall per execution (arrival + post-trade horizons), the policy
  // A/B scoreboard, and the capacity frontier. Everything is SERVER-computed (one TcaSnapshot so
  // every surface shows identical numbers); this tab orders and renders. Read-only by construction.
  // Live wiring mirrors IntegrityTab (nav/decision-tick hints + 30 s poll; KTD7 — SSE is a hint,
  // the numbers on screen always come from the authoritative REST read). 404 → honest empty state.
  import { onMount, onDestroy } from 'svelte';
  import api from '../../../lib/api.js';
  import { selectedOrgId } from '../../../lib/orgContext.js';
  import { navTickSeq, decisionTickSeq } from '../../../lib/autotradeStore.js';
  import PolicyScoreboard from '../tca/PolicyScoreboard.svelte';
  import ShortfallTable from '../tca/ShortfallTable.svelte';
  import CapacityFrontier from '../tca/CapacityFrontier.svelte';

  export let strategy = null;
  export let status = null;

  $: strategyId = strategy?.strategyId ?? null;
  $: isRunning = String(status) === 'Running';

  let snapshot = null; // TcaSnapshot
  let loading = false;
  let error = null;
  let inFlight = false;
  // Mid-flight triggers queue ONE trailing reload (bursts coalesce; an org switch never strands
  // a stale or false-empty view) — the IntegrityTab discipline verbatim.
  let pendingReload = false;

  async function loadAll() {
    if (!strategyId) return;
    if (inFlight) {
      pendingReload = true;
      return;
    }
    inFlight = true;
    loading = true;
    const at = $selectedOrgId;
    const sid = strategyId;
    try {
      const s = await api.getStrategyTca(sid, at);
      // Drop a stale response if the org or strategy moved while it was in flight (scoped-loader rule).
      if ($selectedOrgId !== at || strategy?.strategyId !== sid) return;
      snapshot = s;
      error = null;
    } catch (err) {
      if ($selectedOrgId !== at || strategy?.strategyId !== sid) return;
      const msg = String(err?.message ?? err);
      error = msg.includes('status: 404')
        ? null
        : (msg.includes('details: ') ? msg.slice(msg.indexOf('details: ') + 'details: '.length) : msg);
    } finally {
      loading = false;
      inFlight = false;
      if (pendingReload) {
        pendingReload = false;
        loadAll();
      }
    }
  }

  // (Strategy, org) reload — guard seeded from raw stores, not `$:` values (lesson 2026-07-14).
  let lastKey = '';
  $: {
    const key = `${strategy?.strategyId ?? ''}|${$selectedOrgId ?? ''}`;
    if (key !== lastKey && strategy?.strategyId) {
      lastKey = key;
      snapshot = null;
      loadAll();
    }
  }

  let lastSeq = 0;
  $: if (isRunning && $navTickSeq !== lastSeq) {
    lastSeq = $navTickSeq;
    loadAll();
  }

  let lastDecisionSeq = 0;
  $: if (isRunning && $decisionTickSeq !== lastDecisionSeq) {
    lastDecisionSeq = $decisionTickSeq;
    loadAll();
  }

  const FALLBACK_POLL_MS = 30000;
  let pollTimer = null;
  $: if (isRunning && !pollTimer) {
    pollTimer = setInterval(loadAll, FALLBACK_POLL_MS);
  } else if (!isRunning && pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  onDestroy(() => { if (pollTimer) clearInterval(pollTimer); });
  onMount(loadAll);

  function fmtAsOf(t) {
    const d = new Date(t);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleTimeString();
  }

  $: hasAnything = (snapshot?.executions ?? []).length > 0
    || (snapshot?.scoreboard?.buckets ?? []).length > 0
    || (snapshot?.frontier ?? []).length > 0;
</script>

<div class="tca-root">
  <div class="tca-toolbar">
    {#if isRunning}<span class="tca-live" title="Updating live on each NAV/decision tick">● live</span>{/if}
    {#if snapshot}
      <span class="tca-asof num">
        as of {fmtAsOf(snapshot.asOfUtc)} · marks quoted from the public venue tape, never the engine (ADR-0030){$selectedOrgId ? ' · org view' : ''}
      </span>
    {/if}
    <button type="button" class="btn btn-ghost btn-sm" on:click={loadAll} disabled={loading}>Refresh</button>
  </div>

  {#if error}
    <div class="alert alert-danger" role="alert">{error}
      <button type="button" class="btn btn-ghost btn-sm" on:click={loadAll}>Retry</button>
    </div>
  {:else if loading && !snapshot}
    <div class="at-skeleton-rows"><div class="at-skeleton-row"></div></div>
  {:else if !snapshot || !hasAnything}
    <div class="empty-state">
      <p class="empty-state-description">
        No measured executions{$selectedOrgId ? ' in the selected org' : ''} yet.
        Marks are captured per placement and priced against the quote-tape witness — figures
        appear as orders execute (and horizons fill in up to an hour after each one settles).
      </p>
    </div>
  {:else}
    <section class="tca-panel">
      <h4 class="tca-panel-title">Policy scoreboard (executed A/B)</h4>
      <PolicyScoreboard buckets={snapshot.scoreboard?.buckets ?? []} />
    </section>

    <section class="tca-panel">
      <h4 class="tca-panel-title">Implementation shortfall — per execution</h4>
      <ShortfallTable executions={snapshot.executions ?? []} />
    </section>

    <section class="tca-panel">
      <h4 class="tca-panel-title">Capacity frontier (est.)</h4>
      <CapacityFrontier frontier={snapshot.frontier ?? []} />
    </section>
  {/if}
</div>

<style>
  @import '../../Backtesting/Backtesting.css';
  .tca-root { display: flex; flex-direction: column; gap: 0.85rem; }
  .tca-toolbar { display: flex; align-items: center; gap: 0.75rem; }
  .tca-live { font-size: 0.75rem; color: var(--success, #1aa260); }
  .tca-asof { font-size: 0.68rem; color: var(--text-muted, #8a8b8d); }
  .tca-panel { border: 1px solid var(--border-subtle, #1e1f22); border-radius: 8px; padding: 0.75rem; }
  .tca-panel-title { margin: 0 0 0.5rem; font-size: 0.8rem; color: var(--text-secondary, #b5b6b8); }
  .num { font-variant-numeric: tabular-nums; }
</style>
